import { RefObject, useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import pageBuilderStore from "../../../stores/pageBuilderStore";
import {
  ChevronDown,
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Subscript,
  Superscript,
  List,
  ListOrdered,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Quote,
  Eraser,
  Link,
  Unlink,
  Palette,
  Highlighter,
  Code,
} from "lucide-react";
import { ITypographyRole } from "../../../stores/state-interfaces";
import bodyStylesManager from "../../../stores/body-styles-manager";
import glideBarManager from "../../../stores/glidebar-manager";
import contentManager from "../../../stores/content-manager";
import blocksManager from "../../../stores/blocks-manager";
import {
  IHeadingBlockData,
  ITextBlockData,
} from "../../../types/content-block-data";
import { HexAlphaColorPicker } from "react-colorful";

const headingOptions = [
  { label: "H1", value: 1 },
  { label: "H2", value: 2 },
  { label: "H3", value: 3 },
];

interface GlideBarProps {
  isVisible: boolean;
  contentType: "heading" | "text";
  targetRef: RefObject<HTMLElement>;
  canvasRef: RefObject<HTMLDivElement>;
  selection?: any;
  onFormat?: (command: string, value?: any) => void;
}

/**
 * GlideBar – Floating Formatting Toolbar for PageBuilder
 *
 * @component
 * @description
 * A contextual formatting toolbar that appears above a selected editable content block (e.g., heading or paragraph).
 * It provides text formatting options such as bold, italic, underline, alignment, lists, blockquote, code, links, and more.
 *
 * @features
 * - Floats above the currently selected block using its bounding box (via `targetRef`)
 * - Only one GlideBar exists at a time (singleton pattern)
 * - Supports both "heading" and "text" block types, with context-aware formatting buttons
 * - Formatting actions use `document.execCommand()` (for compatibility)
 * - Button state reflects current selection formatting using `queryCommandState()` in `selectionchange`
 * - Avoids caret/focus loss with `stopPropagation()` and focus targeting logic
 *
 * @props
 * @param {boolean} isVisible
 *   Controls the visibility of the bar. When false or hidden, the component returns null.
 * @param {"heading" | "text"} contentType
 *   Determines which formatting tools should be rendered (e.g., lists/quotes only for "text").
 * @param {React.RefObject<HTMLElement>} targetRef
 *   Ref to the content block being edited — used to calculate positioning.
 * @param {any} [selection]
 *   Reserved for future use — may support range-based styling or custom commands.
 * @param {(command: string, value?: any) => void} [onFormat]
 *   Optional external callback for formatting commands. Currently not used, but available for future enhancements.
 *
 * @remarks
 * - The toolbar uses `position: fixed` and adjusts position on block selection.
 * - Offset (56px by default) ensures visual separation from block content.
 * - Currently uses static icons from Lucide (with conditional state styling).
 * - Dropdown for changing heading level (`H1`/`H2`/`H3`) is integrated and uses MobX store state.
 * - Formatting buttons are grouped and visually separated by dividers for better UX.
 *
 * @future
 * - Auto-flip positioning if not enough vertical space above the block
 * - Horizontal scrolling for narrow viewports or overflowing button groups
 * - Custom color pickers for font/background
 * - Link editing popup (instead of prompt)
 * - Optional compact (condensed) mode for mobile screens
 *
 * @author d2d-weizhi (Chen Weizhi)
 * @since 2025-06-11
 */
export default observer(function GlideBar(props: GlideBarProps) {
  const barRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });
  const [ready, setReady] = useState(false);
  const [formattingState, setFormattingState] = useState({
    bold: false,
    italic: false,
    underline: false,
    strikethrough: false,
    subscript: false,
    superscript: false,
    unorderedList: false,
    orderedList: false,
    blockquote: false,
    justifyLeft: false,
    justifyCenter: false,
    justifyRight: false,
    justifyFull: false,
    link: false,
  });
  const [isSelectionLink, setIsSelectionLink] = useState<boolean>(false);
  const [isSelectionCode, setIsSelectionCode] = useState<boolean>(false);
  const [colorPickerType, setColorPickerType] = useState<"text" | "highlighting" | null>(null);
  const [currColor, setCurrColor] = useState<string | undefined>("#444444");
  const selectionRef = useRef<Range | null>(null);
  const colorPickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleDocumentClick(event: MouseEvent) {
      // If click is inside the bar, color picker, or the current editing block, do nothing
      const contentBlockRef = glideBarManager.getGlideBarState().contentBlockRef?.current;

      if (barRef.current?.contains(event.target as Node) ||
        (colorPickerType && colorPickerRef.current?.contains(event.target as Node)) ||
        (contentBlockRef && contentBlockRef.contains(event.target as Node))
      ) {
        return; // Don't hide, user clicked inside the editing context
      }

      // Otherwise, user clicked outside, hide GlideBar
      setColorPickerType(null);
      glideBarManager.hideGlideBar();
      glideBarManager.setIsEditingHeading(false);
      glideBarManager.setIsEditingText(false);
    }

    document.addEventListener("mousedown", handleDocumentClick);
    return () => {
      document.removeEventListener("mousedown", handleDocumentClick);
    };
  }, [colorPickerType]);

  useEffect(() => {
    if (props.isVisible && props.targetRef?.current && props.canvasRef?.current) {
      const rect = props.targetRef.current.getBoundingClientRect();
      const barWidth = barRef.current?.offsetWidth;
      const padding = 12;
      let left = rect.left + window.scrollX;
      const top = rect.top + window.scrollY - 56;
      const maxRight = window.innerWidth - padding;

      const canvasRect = props.canvasRef.current.getBoundingClientRect()
      if (props.contentType === "heading") {
        if (rect.left < canvasRect.left + 400 && canvasRect.right > rect.right)
          left = rect.left;
        else if (rect.left > canvasRect.left + 600) {
          left = rect.left - 400;
        }
        else if (canvasRect.right - rect.right < 400)
          left = rect.right - 630;
        else
          left = canvasRect.left + 30;
      } else
        left = canvasRect.left + 30;

      setCoords({
        top: top, // 56px offset above block (tweak as needed)
        left: left,
      });
      setReady(true);
    } else {
      setReady(false);
    }
  }, [props.isVisible, props.targetRef?.current]);

  // Used for handling text selection and formatting.
  useEffect(() => {
    const handleSelectionChange = () => {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;

      const selectedText = selection.toString();
      if (selectedText.length > 0) {
        selectionRef.current = selection.getRangeAt(0).cloneRange();
        console.log("🔍 Selected text:", selectedText);
      }

      setFormattingState({
        bold: document.queryCommandState("bold"),
        italic: document.queryCommandState("italic"),
        underline: document.queryCommandState("underline"),
        strikethrough: document.queryCommandState("strikeThrough"),
        subscript: document.queryCommandState("subscript"),
        superscript: document.queryCommandState("superscript"),
        unorderedList: document.queryCommandState("insertUnorderedList"),
        orderedList: document.queryCommandState("insertOrderedList"),
        blockquote: document.queryCommandValue("formatBlock") === "blockquote",
        justifyLeft: document.queryCommandState("justifyLeft"),
        justifyCenter: document.queryCommandState("justifyCenter"),
        justifyRight: document.queryCommandState("justifyRight"),
        justifyFull: document.queryCommandState("justifyFull"),
        link: document.queryCommandState("createLink"),
      });

      setIsSelectionLink(isSelectionInsideLink());
      setIsSelectionCode(isSelectionInsideCode());
    };

    document.addEventListener("selectionchange", handleSelectionChange);
    return () => {
      document.removeEventListener("selectionchange", handleSelectionChange);
    };
  }, []);

  if (!props.isVisible || !ready) return null;

  const block = contentManager.getContentBlockById(glideBarManager.getGlideBarState().contentBlockId!);

  function updateHeadingProperties(field: keyof IHeadingBlockData, value: any) {
    if (glideBarManager.getGlideBarState().blockType === "heading") {
      const headingData = block?.blockData as IHeadingBlockData;
      contentManager.setHeadingConfig(glideBarManager.getGlideBarState().contentBlockId!, {
        ...headingData,
        [field]: value,
      });
    }
  }

  function isSelectionInsideLink() {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return false;

    let node = selection.anchorNode as HTMLElement | null;

    if (node && node.nodeType === Node.TEXT_NODE) node = node.parentElement;
    while (node) {
      if (node.tagName === "A") return true;
      node = node.parentElement;
    }
    return false;
  }

  function isSelectionInsideCode() {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return false;

    let node = selection.anchorNode as HTMLElement | null;
    if (node && node.nodeType === Node.TEXT_NODE) node = node.parentElement;

    while (node) {
      if (node.tagName === "CODE") return true;
      node = node.parentElement;
    }
    return false;
  }

  function toggleInlineCode() {
    const selection = window.getSelection();
    if (!selection  || selection.rangeCount === 0) return;

    let node = selection.anchorNode as HTMLElement | null;
    if (node && node.nodeType === Node.TEXT_NODE) node = node.parentElement;

    // 1. Detect if inside <code>
    let codeNode: HTMLElement | null = null;
    let temp = node;
    while (temp) {
      if (temp.tagName === "CODE") {
        codeNode = temp;
        break;
      }
      temp = temp.parentElement;
    }

    if (codeNode) {
      // 2. If inside <code>, unwrap
      const parent = codeNode.parentNode;
      while (codeNode.firstChild) parent?.insertBefore(codeNode.firstChild, codeNode);
      parent?.removeChild(codeNode);
    } else {
      // 3. If not, wrap selection (or word) with <code>
      const range = selection.getRangeAt(0);

      const codeElem = document.createElement("code");
      try {
        range.surroundContents(codeElem);
      } catch {
        // fallback: insert HTML, or ignore ig selection is invalid.
      }
    }
  }

  /**
   * Traverses up the DOM from the current caret or selection to detect the nearest text color style.
   *
   * - Starts from the anchor node (caret position or start of selection).
   * - If inside a text node, checks the parent element.
   * - Walks up ancestor elements until it finds a <span> with a `color` style applied.
   * - Returns the color as a hex string (using rgbaStringToHex) if found, or null if not.
   *
   * @returns The current text color at the caret/selection as a hex string (e.g., "#ff0000"), or null if no color is set.
   *
   * @example
   * // Returns "#00ff00" if caret is inside <span style="color: rgb(0,255,0)">...</span>
   * const color = getCurrentTextColor();
   */
  function getCurrentTextColor() {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return null;

    // Check anchor node for caret, or common ancestor for selection
    let node = selection.anchorNode;
    if (node && node.nodeType === Node.TEXT_NODE) {
      node = node.parentElement;
    }

    while (node && node !== document) {
      if (node instanceof HTMLElement && node.tagName === "SPAN") {
        if (node.style.color) return rgbaStringToHex(node.style.color);
      }
      node = node.parentElement;
    }
    return null;
  }

  /**
   * Traverses up the DOM from the current caret or selection to find the nearest background color style.
   *
   * - Starts from the anchor node (caret position or start of selection).
   * - If inside a text node, checks the parent element.
   * - Walks up ancestor elements until it finds a <span> with a `backgroundColor` style applied.
   * - Returns the background color as a hex string (using rgbaStringToHex) if found, or null if not.
   *
   * @returns The current background color at the caret/selection as a hex string (e.g., "#ffff00"), or null if no background color is set.
   *
   * @example
   * // Returns "#fffae5" if caret is inside <span style="background-color: rgb(255,250,229)">...</span>
   * const bgColor = getCurrentBGColor();
   */
  function getCurrentBGColor() {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return null;

    // Check anchor node for caret, or common ancestor for selection
    let node = selection.anchorNode;
    if (node && node.nodeType === Node.TEXT_NODE) {
      node = node.parentElement;
    }

    while (node && node !== document) {
      if (node instanceof HTMLElement && node.tagName === "SPAN") {
        if (node.style.backgroundColor) return rgbaStringToHex(node.style.backgroundColor);
      }
      node = node.parentElement;
    }
    return null;
  }

  /**
   * Applies a text or background color to the current selection or caret within a contentEditable block.
   *
   * - If the selection/caret is inside an existing <span>, updates the relevant style (color or backgroundColor) on that span (to avoid unnecessary nesting).
   * - If not, wraps the current selection with a new <span> styled with the chosen color.
   * - If the selection cannot be wrapped due to browser/contentEditable constraints, falls back to `document.execCommand` for legacy support.
   *
   * @param color - The color to apply, in any valid CSS format (will be converted to hex).
   * @param type - "text" to apply color, "highlighting" or "background" to apply background color.
   *
   * @example
   * // To apply text color:
   * applyColorToSelection("#ff0000", "text");
   *
   * // To apply yellow highlighting:
   * applyColorToSelection("#ffff00", "highlighting");
   */
  function applyColorToSelection(color: string, type: string) {
    const selection = window.getSelection();
    if (!selection?.rangeCount) return;
    const range = selection.getRangeAt(0);
    const hexColor:string = rgbaStringToHex(color);

    let node = selection.anchorNode;
    if (node && node.nodeType === Node.TEXT_NODE) {
      node = node.parentElement;
    }

    // Check if selection is inside a span
    if (node instanceof HTMLElement && node.tagName === "SPAN") {
      // Update the relevant style (add or replace)
      if (type === "text") node.style.color = hexColor;
      else if (type === "highlighting" || type === "background") node.style.backgroundColor = hexColor;
      return;
    }

    // Otherwise, wrap selection in a new span with the relevant style
    const span = document.createElement("span");
    if (type === "text") span.style.color = hexColor;
    if (type === "highlighting" || type === "background") span.style.backgroundColor = hexColor;

    try {
      range.surroundContents(span);
    } catch (e) {
      document.execCommand(type === "text" ? "foreColor" : "hiliteColor", false, hexColor);
    }
  }

  function rgbaStringToHex(rgba: string): string {
    // Match rgb or rgba and extract numbers
    const match = rgba.trim().match(/^rgba?\((\d+), ?(\d+), ?(\d+)(?:, ?([0-9.]+))?\)$/);

    console.log(match);

    if (!match) return rgba; // fallback, maybe it's already hex

    let [, r, g, b, a] = match;
    r = parseInt(r).toString(16).padStart(2, '0');
    g = parseInt(g).toString(16).padStart(2, '0');
    b = parseInt(b).toString(16).padStart(2, '0');
    let alpha = '';

    if (typeof a !== "undefined") {
      a = Math.round(parseFloat(a) * 255).toString();
      alpha = a.toString().padStart(2, '0');
    }

    return `#${r}${g}${b}${alpha}`;
  }

  function removeEmptySpans(container: HTMLElement) {
    // Get all span elements inside the container (contentEditable block)
    const spans = container.querySelectorAll('span');
    spans.forEach(span => {
      if (span.textContent === '' && span.children.length === 0) {
        span.parentNode?.removeChild(span);
      }
    });
  }

  /**
   * Did the user select a custom field?
   */
  function selectionIntersectsCF(): boolean {
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return false;
    
    const r  =sel.getRangeAt(0);
    const frag = r.cloneContents();
    // @ts-ignore
    return !!frag.querySelector?.('span[data-custom-field="true"]');
  }

  function wrapSelectionWithTag(tagName: 'b' | 'strong' | 'i' | 'u' | 's' | 'sub' | 'sup') {
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return;

    // ensure the contentEditable target has focus
    const active = glideBarManager.getGlideBarState().contentBlockRef?.current;
    if (active && document.activeElement !== active) active.focus();

    const range = sel.getRangeAt(0);

    if (range.collapsed) return;

    // Extract the exact selection (order preserved, CF spans untouched)
    const frag = range.extractContents();
    const wrapper = document.createElement(tagName);
    wrapper.appendChild(frag);

    // Insert wrapper at the original start
    range.insertNode(wrapper);

    // Move caret to after the wrapper to keep typing smooth
    sel.removeAllRanges();
    const after = document.createRange();
    after.setStartAfter(wrapper);
    after.collapse(true);

    sel.addRange(after);
  }

  function safeInlineFormat(kind: "bold" | "italic" | "underline" | "strikeThrough" | "sub" | "sup") {
    // If selection touches a custom-field, avoid execCommand
    if (selectionIntersectsCF()) {
      if (kind === "bold") wrapSelectionWithTag("strong"); // or 'b'
      else if (kind === "italic") wrapSelectionWithTag("i");
      else if (kind === "underline") wrapSelectionWithTag("u");
      else if (kind === "strikeThrough") wrapSelectionWithTag("s");
      else if (kind === "sub") wrapSelectionWithTag("sub");
      else if (kind === "sup") wrapSelectionWithTag("sup");
      return;
    }

    // Otherwise, normal behavior is fine
    const map: Record<typeof kind, string> = {
      bold: "bold", italic: "italic", underline: "underline", strikeThrough: "strikeThrough", sub: "subscript", sup: "superscript"
    };
    document.execCommand(map[kind], false);
  }

  function safeExecCommand(cmd: string, value?: string) {
    const active = glideBarManager.getGlideBarState().contentBlockRef?.current;
    if (active && document.activeElement !== active) active.focus();

    document.execCommand(cmd, false, value ?? undefined);
  }

  return (
    <>
      <div
        ref={barRef}
        className="
        glidebar
        z-50
        bg-gradient-to-b from-white to-gray-100
        shadow-xl
        rounded-lg
        py-2 px-4
        flex items-center gap-2
        border border-gray-200
        pointer-events-auto
				w-max
      "
        style={{
          position: "fixed",
          top: coords.top - 20,
          left: coords.left,
          boxShadow:
            "0 6px 16px -4px rgba(0,0,0,0.10), 0 2px 4px 0 rgba(0,0,0,0.06)",
          // Optionally, transition: 'top 0.1s, left 0.1s'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {props.contentType === "heading" && (
          <>
            <div className="mr-2">
              <button
                type="button"
                className="flex items-center gap-1 px-3 py-2 min-w-[36px] min-h-[36px] bg-gray-50 hover:bg-gray-100 rounded-md font-semibold text-gray-700 transition border border-gray-200"
                onMouseDown={(e) => {
                  e.stopPropagation();
                  glideBarManager.setDropDownOpen(
                    !glideBarManager.getDropDownOpen()
                  );
                  console.log("Dropdown triggered on GlideBar...");
                }}
                tabIndex={0}
              >
                {pageBuilderStore.glideBarState.currentHeading}{" "}
                <ChevronDown size={18} className="ml-1" />
              </button>
              {glideBarManager.getDropDownOpen() && (
                <div className="absolute left-0 top-full mt-1 w-28 bg-white border border-gray-200 rounded shadow-lg">
                  {["H1", "H2", "H3", "H4", "H5", "H6"].map((heading, idx) => (
                    <button
                      key={heading}
                      type="button"
                      className={`block w-full text-left px-4 py-2 hover:bg-gray-100 ${
                        glideBarManager.getCurrentHeading() === heading
                          ? "bg-gray-50 font-bold"
                          : ""
                      }`}
                      onClick={() => {
                        const headingLevel = heading.toLowerCase();
                        const headingDefaults =
                          bodyStylesManager.getBodyStylesState().typography![
                            headingLevel as keyof ITypographyRole
                          ];

                        // We will only update fontSize from bodyStylesState when heading level changes.
                        // Preserve all other existing inline/GlideBar formatting.
                        updateHeadingProperties(
                          "fontSize",
                          headingDefaults.fontSize || { value: "2", unit: "rem" }
                        );

                        glideBarManager.setCurrentHeading(heading);
                        glideBarManager.setDropDownOpen(false);
                        // You can later call a callback/prop to actually change heading level in logic
                      }}
                    >
                      {heading}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <span className="glidebar-divider mx-2 h-5 border-l border-gray-200" />
          </>
        )}

        {/* Font Style */}
        <button
          className={`p-2.5 min-w-[36px] min-h-[36px] flex items-center justify-center ${formattingState.bold ? "bg-gray-200" : "hover:bg-gray-100"} rounded-md transition`}
          onMouseDown={(e) => {
            e.stopPropagation();
            safeInlineFormat("bold");
          }}
        >
          <Bold size={24} />
        </button>
        <button
          className={`p-2.5 min-w-[36px] min-h-[36px] flex items-center justify-center ${formattingState.italic ? "bg-gray-200" : "hover:bg-gray-100"} rounded-md transition`}
          onMouseDown={(e) => {
            e.stopPropagation();
            safeInlineFormat("italic");
          }}
        >
          <Italic size={24} />
        </button>
        <button
          className={`p-2.5 min-w-[36px] min-h-[36px] flex items-center justify-center ${formattingState.underline ? "bg-gray-200" : "hover:bg-gray-100"} rounded-md transition`}
          onMouseDown={(e) => {
            e.stopPropagation();
            safeInlineFormat("underline");
          }}
        >
          <Underline size={24} />
        </button>
        <button
          className={`p-2.5 min-w-[36px] min-h-[36px] flex items-center justify-center ${formattingState.strikethrough ? "bg-gray-200" : "hover:bg-gray-100"} rounded-md transition`}
          onMouseDown={(e) => {
            e.stopPropagation();
            safeInlineFormat("strikeThrough");
          }}
        >
          <Strikethrough size={24} />
        </button>
        <span className="glidebar-divider mx-2 h-5 border-l border-gray-200" />

        {/* Scripts */}
        <button
          className={`p-2.5 min-w-[36px] min-h-[36px] flex items-center justify-center ${formattingState.subscript ? "bg-gray-200" : "hover:bg-gray-100"} rounded-md transition`}
          onMouseDown={(e) => {
            e.stopPropagation();
            safeInlineFormat("sub");
          }}
        >
          <Subscript size={24} />
        </button>
        <button
          className={`p-2.5 min-w-[36px] min-h-[36px] flex items-center justify-center ${formattingState.superscript ? "bg-gray-200" : "hover:bg-gray-100"} rounded-md transition`}
          onMouseDown={(e) => {
            e.stopPropagation();
            safeInlineFormat("sup");
          }}
        >
          <Superscript size={24} />
        </button>
        <span className="glidebar-divider mx-2 h-5 border-l border-gray-200" />

        {/* Lists */}
        {props.contentType === "text" && (
          <>
            <button
              className={`p-2.5 min-w-[36px] min-h-[36px] flex items-center justify-center ${formattingState.unorderedList ? "bg-gray-200" : "hover:bg-gray-100"} rounded-md transition`}
              onMouseDown={(e) => {
                e.stopPropagation();
                safeExecCommand("insertUnorderedList");
              }}
            >
              <List size={24} />
            </button>
            <button
              className={`p-2.5 min-w-[36px] min-h-[36px] flex items-center justify-center ${formattingState.orderedList ? "bg-gray-200" : "hover:bg-gray-100"} rounded-md transition`}
              onMouseDown={(e) => {
                e.stopPropagation();
                safeExecCommand("insertOrderedList");
              }}
            >
              <ListOrdered size={24} />
            </button>
            <span className="glidebar-divider mx-2 h-5 border-l border-gray-200" />
          </>
        )}

        {/* Align */}
        <button
          className={`p-2.5 min-w-[36px] min-h-[36px] flex items-center justify-center ${formattingState.justifyLeft ? "bg-gray-200" : "hover:bg-gray-100"} rounded-md transition`}
          onMouseDown={(e) => {
            e.stopPropagation();
            safeExecCommand("justifyLeft");
            const block = contentManager.getContentBlockById(
              blocksManager.getBlockEditing().selectedBlockId!
            );

            // Sync the alignment settings with the block's style settings.
            if (block?.blockType === "heading") {
              const headingData = block.blockData as IHeadingBlockData;

              contentManager.setHeadingConfig(block.contentBlockId, {
                ...headingData,
                alignment: "left",
              });
            } else if (block?.blockType === "text") {
              const textData = block.blockData as ITextBlockData;

              contentManager.setTextConfig(block.contentBlockId, {
                ...textData,
                alignment: "left",
              });
            }
          }}
        >
          <AlignLeft size={24} />
        </button>
        <button
          className={`p-2.5 min-w-[36px] min-h-[36px] flex items-center justify-center ${formattingState.justifyCenter ? "bg-gray-200" : "hover:bg-gray-100"} rounded-md transition`}
          onMouseDown={(e) => {
            e.stopPropagation();
            safeExecCommand("justifyCenter");

            const block = contentManager.getContentBlockById(
              blocksManager.getBlockEditing().selectedBlockId!
            );

            // Sync the alignment settings with the block's style settings.
            if (block?.blockType === "heading") {
              
              const headingData = block.blockData as IHeadingBlockData;

              contentManager.setHeadingConfig(block.contentBlockId, {
                ...headingData,
                alignment: "center",
              });
            } else if (block?.blockType === "text") {
              const textData = block.blockData as ITextBlockData;

              contentManager.setTextConfig(block.contentBlockId, {
                ...textData,
                alignment: "center",
              });
            }
          }}
        >
          <AlignCenter size={24} />
        </button>
        <button
          className={`p-2.5 min-w-[36px] min-h-[36px] flex items-center justify-center ${formattingState.justifyRight ? "bg-gray-200" : "hover:bg-gray-100"} rounded-md transition`}
          onMouseDown={(e) => {
            e.stopPropagation();
            safeExecCommand("justifyRight");

            const block = contentManager.getContentBlockById(
              blocksManager.getBlockEditing().selectedBlockId!
            );

            // Sync the alignment settings with the block's style settings.
            if (block?.blockType === "heading") {
              const headingData = block.blockData as IHeadingBlockData;

              contentManager.setHeadingConfig(block.contentBlockId, {
                ...headingData,
                alignment: "right",
              });
            } else if (block?.blockType === "text") {
              const textData = block.blockData as ITextBlockData;

              contentManager.setTextConfig(block.contentBlockId, {
                ...textData,
                alignment: "right",
              });
            }
          }}
        >
          <AlignRight size={24} />
        </button>
        <span className="glidebar-divider mx-2 h-5 border-l border-gray-200" />

        {props.contentType === "text" && (
          <>
            {/* Quote/Code */}
            <button
              className={`p-2.5 min-w-[36px] min-h-[36px] flex items-center justify-center ${formattingState.blockquote ? "bg-gray-200" : "hover:bg-gray-100"} rounded-md transition`}
              onMouseDown={(e) => {
                e.stopPropagation();
                safeExecCommand("formatBlock", "blockquote");
              }}
            >
              <Quote size={24} />
            </button>
            <button
              className={`p-2.5 min-w-[36px] min-h-[36px] flex items-center justify-center ${isSelectionCode ? "bg-gray-200" : "hover:bg-gray-100"} rounded-md transition`}
              onMouseDown={(e) => {
                e.stopPropagation();
                toggleInlineCode();
              }}
            >
              <Code size={24} />
            </button>
            <span className="glidebar-divider mx-2 h-5 border-l border-gray-200" />

            {/* Link */}
            {!isSelectionLink && 
              <button
                className={`p-2.5 min-w-[36px] min-h-[36px] flex items-center justify-center ${formattingState.link ? "bg-gray-200" : "hover:bg-gray-100"} rounded-md transition`}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  const url = prompt("Enter URL:");
                  if (url) {
                    safeExecCommand("createLink", url);
                  }
                }}
              >
                <Link size={24} />
              </button>
            }
              
            <button
              className={`p-2.5 min-w-[36px] min-h-[36px] flex items-center justify-center hover:bg-gray-100 rounded-md transition`}
              onMouseDown={(e) => {
                e.stopPropagation();
                safeExecCommand("unlink");
              }}
            >
              <Unlink size={24} />
            </button>
            <span className="glidebar-divider mx-2 h-5 border-l border-gray-200" />
          </>
        )}

        {/* Colors */}
        <button
          className={`p-2.5 min-w-[36px] min-h-[36px] flex items-center justify-center hover:bg-gray-100 rounded-md transition`}
          onMouseDown={e => {
            e.stopPropagation();
            const selection = window.getSelection();
            if (selection && selection.rangeCount > 0) {
              selectionRef.current = selection.getRangeAt(0).cloneRange();
            }

            if (colorPickerType === "text")
              setColorPickerType(null);
            else {
              setCurrColor(getCurrentTextColor() || "#444444");
              setColorPickerType("text");
            }
          }}
        >
          <Palette size={24} />
        </button>
        <button
          className={`p-2.5 min-w-[36px] min-h-[36px] flex items-center justify-center hover:bg-gray-100 rounded-md transition`}
          onMouseDown={e => {
            e.stopPropagation();
            const selection = window.getSelection();
            if (selection && selection.rangeCount > 0) {
              selectionRef.current = selection.getRangeAt(0).cloneRange();
            }

            if (colorPickerType === "highlighting")
              setColorPickerType(null);
            else {
              setCurrColor(getCurrentBGColor() || "#00000000");
              setColorPickerType("highlighting");
            }
          }}
        >
          <Highlighter size={24} />
        </button>
        {colorPickerType && (
          <div
            ref={colorPickerRef}
            className="
                        absolute z-40 right-0 top-[64px] w-64 rounded-xl shadow-xl
                        bg-white border border-gray-200 p-2 flex flex-col gap-3
                        animate-in
                      "
            style={{ background: "#f8fafc" }}
            onClick={(e) => e.stopPropagation()}
          >
            <HexAlphaColorPicker
              color={currColor}
              onMouseLeave={() => {
                console.log("color:", currColor!);
                applyColorToSelection(rgbaStringToHex(currColor!), colorPickerType);
                setColorPickerType(null);
              }}
              onChange={(color: string) => {
                // const selection = window.getSelection();
                // if (selectionRef.current) {
                //   selection?.removeAllRanges();
                //   selection?.addRange(selectionRef.current);
                // }
                
                setCurrColor(color);
              }}
              style={{
                width: "100%",
                height: "180px",
                borderRadius: "0.75rem",
                boxShadow: "0 1.5px 7px 0 rgba(30, 41, 59, 0.06)",
              }}
              // You can extend with hue, alpha, swatches, etc.
            />
            {/* Optionally: Add a swatch row here */}
          </div>
        )}
        <span className="glidebar-divider mx-2 h-5 border-l border-gray-200" />

        {/* Remove Formatting */}
        <button
          className={`p-2.5 min-w-[36px] min-h-[36px] flex items-center justify-center hover:bg-gray-100 rounded-md transition`}
          onMouseDown={(e) => {
            e.stopPropagation();
            safeExecCommand("removeFormat");
            const ref = glideBarManager.getGlideBarState().contentBlockRef?.current;
            if (ref) removeEmptySpans(ref);
          }}
        >
          <Eraser size={24} />
        </button>
      </div>
    </>
  );
});
